/**
 * @file use-category-form-state.ts
 *
 * Controlled-form state for the category form (no react-hook-form). Holds the
 * editable fields, exposes a `patch` updater, tracks whether the form is dirty
 * against its seed, and runs hand-rolled validation (required name; a safety-net
 * check that the chosen parent is not the category itself or a descendant — the
 * select already hides those, this guards against a stale value). Errors surface
 * only after the first `validate()` call so the form doesn't shout at an
 * untouched field.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import type { ShopCategory } from '../../api/shop-types';
import { adminMessages } from '../../i18n/messages';
import { deriveFields, shallowEqual } from './category-form-utils';
import type { CategoryFormErrors, CategoryFormFields } from './category-form-types';

/** What {@link useCategoryFormState} returns to the form. */
export interface UseCategoryFormStateResult {
  fields: CategoryFormFields;
  patch: (update: Partial<CategoryFormFields>) => void;
  isDirty: boolean;
  errors: CategoryFormErrors;
  validate: () => boolean;
  /** Attach to the name field; focused/scrolled to when validation fails. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the category form's field state and validation.
 *
 * @param category - The seed category (edit), or `undefined` to create.
 * @param forbiddenParentIds - Ids that may not be chosen as the parent (the
 *   category itself and its descendants); used for the cycle-prevention check.
 *
 * @returns The fields, updater, dirty flag, errors, and a `validate` function.
 */
export function useCategoryFormState(
  category: ShopCategory | undefined,
  forbiddenParentIds: Set<string> = new Set(),
): UseCategoryFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<CategoryFormFields>(() => deriveFields(category));
  const initialRef = useRef<CategoryFormFields>(fields);
  const seededCategoryRef = useRef(category);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Re-seed only when the source category reference changes (e.g. it resolves
  // after a fetch); the initial value is already seeded by `useState`.
  useEffect(() => {
    if (seededCategoryRef.current === category) {
      return;
    }
    seededCategoryRef.current = category;
    const seeded = deriveFields(category);
    setFields(seeded);
    initialRef.current = seeded;
    setSubmitted(false);
  }, [category]);

  const patch = useCallback((update: Partial<CategoryFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: CategoryFormFields): CategoryFormErrors => {
      const errors: CategoryFormErrors = {};
      if (current.name.trim() === '') {
        errors.nameError = intl.formatMessage(adminMessages.categoriesNameRequired);
      }
      if (current.parent && forbiddenParentIds.has(current.parent)) {
        errors.parentError = intl.formatMessage(adminMessages.categoriesParentInvalid);
      }
      return errors;
    },
    [intl, forbiddenParentIds],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.nameError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(() => !shallowEqual(fields, initialRef.current), [fields]);

  return { fields, patch, isDirty, errors, validate, firstErrorRef };
}
